/**
 * Shared state for the citation-preview pane (ADR 0018 Phase D).
 *
 * The pane lives at App-level (right of the chat column) but the
 * "pin a source" action originates inside `MessageBubble` where the
 * `[N]` citation buttons live. Passing a callback through props +
 * emit boundaries got messy fast — a tiny reactive module instead
 * gives every component the same source of truth without a global
 * store dependency.
 *
 * Components use it like a hook:
 *
 *   import { sourcePane } from "../stores/source-pane";
 *   sourcePane.pin({ chunkId: "...", turnId: "t1", hit });
 *   sourcePane.clear();
 *
 * `sourcePane.state` is the reactive ref the pane component watches.
 */
import { reactive } from "vue";

import type { Hit } from "../types/chat";

export interface PinnedSource {
  /** ES chunk id (e.g. `cites-demo--01-3588b4e9`). Drives the
   * `/api/chunks/{id}/context` fetch. */
  chunkId: string;
  /** Owning turn id — used so MessageBubble can keep its aria-pressed
   * state in sync with what's pinned. */
  turnId: string;
  /** The Hit that was clicked. Carries title, section_title, cites,
   * topics — enough for the pane's header to render before the
   * context fetch completes. */
  hit: Hit;
  /** The assistant answer this citation was clicked from. The preview
   * pane uses it to mark the passages of the chunk the answer actually
   * drew on — without it the reader gets a whole reference table and no
   * indication of which row mattered. Optional: callers that reuse the
   * bubble outside a live turn (export pane, transcript) have no answer
   * to offer, and the pane simply renders unmarked. */
  answerText?: string;
}

interface SourcePaneState {
  pinned: PinnedSource | null;
}

const state = reactive<SourcePaneState>({
  pinned: null,
});

export const sourcePane = {
  state,
  /** Pin a new source (or swap to a different one). Idempotent on
   * the same chunkId — re-pinning the same one is a no-op so a
   * second click doesn't trigger a context re-fetch. */
  pin(s: PinnedSource): void {
    if (state.pinned?.chunkId === s.chunkId) return;
    state.pinned = s;
  },
  /** Close the pane. Called by the × button + on Esc. */
  clear(): void {
    state.pinned = null;
  },
  /** True if the given chunkId is currently pinned — drives the
   * `[N]` button's aria-pressed state. */
  isPinned(chunkId: string): boolean {
    return state.pinned?.chunkId === chunkId;
  },
};
